import { basePath } from "@Common/constants";
import type { GetApplicationListOneResponseI } from "@Common/types/bo/applications/applications";
import type { ComponentPropI } from "@Common/types/resources";
import { Button, Chip, Stack } from "@mui/material";
import {
	Datagrid,
	DeleteWithConfirmButton,
	FunctionField,
	List,
	NullableBooleanInput,
	ReferenceArrayInput,
	SearchInput,
	ShowButton,
	TextField,
	TextInput,
	useNotify,
	useRefresh,
	useUpdate,
} from "react-admin";
import { Link } from "react-router-dom";

const applicationsFilters = [
	<ReferenceArrayInput
		reference="forms"
		label="Form"
		source="form_id"
		key="form_id"
	/>,
	<NullableBooleanInput
		label="Has finished responding"
		source="has_finished_responding"
		key="has_finished_responding"
	/>,
	<TextInput label="Email" source="email" key="email" />,
	<NullableBooleanInput
		label="Is archived"
		source="is_archived"
		key="is_archived"
	/>,
];

export const ctaText = "Response over monthly quota - Upgrade to view";

export const renderEarlySubmitIndicators = (
	record: GetApplicationListOneResponseI,
) => {
	const indicators = [];

	if (record.has_left_tab) {
		indicators.push(
			<Chip key="full-screen" label="Left Tab" color="error" size="small" />,
		);
	}

	if (record.has_form_timer_expired) {
		indicators.push(
			<Chip
				key="keyboard"
				label="Form Timer Expired"
				color="error"
				size="small"
			/>,
		);
	}

	if (indicators.length === 0) {
		return "-";
	}

	return (
		<Stack direction="row" spacing={1}>
			{indicators}
		</Stack>
	);
};

const ArchiveButton = ({
	record,
}: {
	record: GetApplicationListOneResponseI;
}) => {
	const notify = useNotify();
	const refresh = useRefresh();
	const [update, { isLoading }] = useUpdate();

	const handleArchive = () => {
		update(
			"applications",
			{
				id: record.id,
				data: { is_archived: !record.is_archived },
				meta: { endpoint: `${record.id}` },
			},
			{
				onSuccess: () => {
					notify("Application archived successfully", { type: "success" });
					refresh();
				},
				onError: () => {
					notify("Error archiving application", { type: "error" });
				},
			},
		);
	};

	return (
		<Button
			onClick={handleArchive}
			disabled={isLoading}
			variant="outlined"
			size="small"
			color="primary"
		>
			{record.is_archived ? "Unarchive" : "Archive"}
		</Button>
	);
};

export const renderActionsField = (record: GetApplicationListOneResponseI) => {
	if (record.over_quota) {
		return (
			<Button
				component={Link}
				to={`${basePath}/settings`}
				variant="contained"
				color="warning"
				size="small"
			>
				{ctaText}
			</Button>
		);
	}
	return (
		<Stack direction="row" spacing={1}>
			<ShowButton />
			<ArchiveButton record={record} />
		</Stack>
	);
};

const renderArchivedIndicator = (record: GetApplicationListOneResponseI) => {
	if (record.is_archived) {
		return <Chip label="Archived" color="default" size="small" />;
	}
	return "-";
};

export const ApplicationsList = (props: ComponentPropI) => {
	const searchFilters = [<SearchInput source="q" alwaysOn key="q" />];

	return (
		<List
			disableAuthentication={props.isPublic}
			perPage={10}
			filters={[...searchFilters, ...applicationsFilters]}
			filterDefaultValues={{ is_archived: false }}
			sort={{ field: "id", order: "DESC" }}
		>
			<Datagrid rowClick={false}>
				<TextField source="id" />
				<TextField source="form_name" sortable={false} />
				<TextField source="email" sortable={false} />
				<TextField
					source="request_origin"
					label="Est. Geolocation (IP)"
					sortable={false}
				/>
				<TextField
					source="browser_origin"
					label="Est. Geolocation (Browser)"
					sortable={false}
				/>
				<FunctionField
					source="browser_user_agent"
					label="User Agent"
					sortable={false}
					render={(record: GetApplicationListOneResponseI) => {
						const ua = record.browser_user_agent;
						if (!ua) return "";
						return ua.length > 60 ? `${ua.substring(0, 60)}…` : ua;
					}}
				/>
				<TextField
					source="has_finished_responding"
					label="Has finished responding"
					sortable={false}
				/>
				<TextField source="created" sortable={false} />
				<FunctionField
					label="Problems"
					render={renderEarlySubmitIndicators}
					sortable={false}
				/>
				<FunctionField
					label="Status"
					render={renderArchivedIndicator}
					sortable={false}
				/>
				<FunctionField label="Actions" render={renderActionsField} />
				<DeleteWithConfirmButton
					mutationMode="pessimistic"
					confirmTitle="Delete this response?"
					confirmContent="Are you sure you want to delete this response? This will allow this email to submit a response again."
				/>
			</Datagrid>
		</List>
	);
};
